import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Select, Input } from 'components/FormFields';
import { ModalV2 } from 'components/Modal';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.background.primary
      : props.theme.colors.background.primary};
  color: ${(props) => props.theme.colors.text.primary};
  font-family: ${(props) => props.theme.fonts.primary};
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 60px 60px 120px;

  @media (max-width: 768px) {
    padding: 60px 24px 120px;
  }
`;

const Title = styled.h1`
  font-size: 56px;
  font-weight: 700;
  margin-bottom: 24px;
  line-height: 1.1;
  color: ${(props) => props.theme.colors.text.primary};
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0 0 48px 0;
  max-width: 800px;
`;

const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 20px;
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const CardWrapper = styled.div`
  margin-bottom: 16px;
`;

const CardContent = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0 0 16px 0;
`;

const PlatformSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
`;

const PlatformOption = styled.button`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  padding: 20px;
  padding-left: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 8px 0 0 8px;
    background: transparent;
    transition: all 0.2s;
  }

  ${(props) => {
    if (!props.selected) return '';
    const bgColor =
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)';
    const borderColor =
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.15)';
    return `
      background: ${bgColor};
      border-color: ${borderColor};

      &::before {
        background: #fa4616;
      }

      &::after {
        content: '✓';
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        background: #fa4616;
        color: #ffffff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 700;
      }
    `;
  }}

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.03)'};
  }
`;

const PlatformIcon = styled.div`
  width: 56px;
  height: 56px;
  background: ${(props) =>
    props.selected
      ? 'rgba(250, 70, 22, 0.2)'
      : props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)'};
  color: ${(props) =>
    props.selected
      ? props.theme.colors.primary
      : props.theme.colors.textGray.muted};
  border: ${(props) =>
    props.selected ? `2px solid ${props.theme.colors.primary}` : 'none'};
  border-radius: 8px;
  flex-shrink: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
`;

const PlatformInfo = styled.div`
  text-align: left;
  flex: 1;
`;

const PlatformName = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${(props) =>
    props.selected
      ? props.theme.colors.text.primary
      : props.theme.name === 'dark'
      ? '#D1D5DB'
      : '#4B5563'};
  margin-bottom: 4px;
`;

const PlatformFees = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.colors.textGray.muted};
  opacity: 0.7;
`;

// Theme-aware input components
const InputWrapper = styled.div`
  width: 100%;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 15px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.text.primary};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#ffffff'};
  border: 1px solid ${(props) => props.theme.colors.card.border};
  border-radius: 6px;
  outline: none;
  transition: all 0.2s;
  font-family: ${(props) => props.theme.fonts.primary};
  box-sizing: border-box;

  &:focus {
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(250, 70, 22, 0.1)'
          : 'rgba(250, 70, 22, 0.1)'};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.textGray.muted};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InputDescription = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.colors.textGray.muted};
  margin-top: 6px;
  line-height: 1.4;
`;

const SliderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SliderCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  padding: 16px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${(props) => props.accentColor};
  }
`;

const SliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SliderLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
`;

const SliderValue = styled.span`
  font-weight: 700;
  font-size: 20px;
  color: ${(props) => props.color};
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0'};
  outline: none;
  -webkit-appearance: none;
  margin-bottom: 8px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${(props) => props.thumbColor};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${(props) => props.thumbColor};
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const HelpText = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.colors.textGray.muted};
`;

const WalkUpCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  padding: 16px;
  position: relative;
  overflow: hidden;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: #ef4444;
  }
`;

const WalkUpValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #ef4444;
  margin-bottom: 8px;
`;

const TicketCountGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const TicketCountCard = styled.div`
  background: ${(props) => props.bg};
  border: 1px solid ${(props) => props.borderColor};
  border-radius: 8px;
  padding: 20px 16px;
  text-align: center;
`;

const TicketCount = styled.div`
  font-size: 42px;
  font-weight: 700;
  color: ${(props) => props.color};
  margin-bottom: 8px;
`;

const TicketLabel = styled.div`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.text.primary
      : props.theme.colors.text.primary};
  font-weight: 500;
  opacity: 0.8;
`;

const StickyResults = styled.div`
  position: sticky;
  top: 20px;

  @media (max-width: 1200px) {
    position: relative;
    top: 0;
  }
`;

const ResultsPanel = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors.card.background
      : '#ffffff'};
  border: 1px solid ${(props) => props.theme.colors.card.border};
  border-radius: 12px;
  padding: 24px;
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const ResultsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0 0 16px 0;
`;

const ResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0 10px 12px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  position: relative;

  ${(props) =>
    props.accentColor &&
    `
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 16px;
      border-radius: 2px;
      background: ${props.accentColor};
    }
  `}
`;

const ResultLabel = styled.span`
  color: ${(props) => (props.theme.name === 'dark' ? '#E5E7EB' : '#4B5563')};
  font-size: 13px;
`;

const ResultValue = styled.span`
  font-weight: 600;
  color: ${(props) => {
    if (props.isPositive) return props.theme.colors.success;
    if (props.isNegative) return '#EF4444';
    return props.theme.colors.text.primary;
  }};
  font-size: 14px;
`;

const SavingsCard = styled.div`
  margin-top: ${(props) => (props.first ? '20px' : '12px')};
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-radius: 10px;
  gap: 16px;
  background: ${(props) =>
    props.variant === 'early'
      ? props.theme.name === 'dark'
        ? 'rgba(59, 130, 246, 0.1)'
        : 'rgba(59, 130, 246, 0.08)'
      : props.theme.name === 'dark'
      ? 'rgba(34, 197, 94, 0.1)'
      : 'rgba(34, 197, 94, 0.08)'};
  border: 1px solid
    ${(props) =>
      props.variant === 'early'
        ? 'rgba(59, 130, 246, 0.3)'
        : 'rgba(34, 197, 94, 0.3)'};
`;

const SavingsIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${(props) => (props.variant === 'early' ? '#3B82F6' : '#22C55E')};

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const SavingsContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SavingsTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
  color: ${(props) => props.theme.colors.text.primary};
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SavingsAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => (props.variant === 'early' ? '#3B82F6' : '#22C55E')};
`;

const SavingsBadge = styled.div`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.8)'};
  color: ${(props) => props.theme.colors.text.primary};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ButtonGroup = styled.div`
  margin-top: 24px;
  display: grid;
  gap: 12px;
`;

const Footer = styled.footer`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(26, 26, 46, 0.95)'
      : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(12px);
  border-top: 1px solid ${(props) => props.theme.colors.border};
  padding: 24px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;

  @media (max-width: 768px) {
    padding: 20px 24px;
    flex-direction: column;
    gap: 16px;
  }
`;

const Progress = styled.div`
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: 14px;
  font-weight: 500;
`;

const FooterButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  background: ${(props) =>
    props.primary ? props.theme.colors.primary : 'transparent'};
  border: ${(props) =>
    props.primary ? 'none' : `1px solid ${props.theme.colors.border}`};
  color: ${(props) =>
    props.primary ? '#ffffff' : props.theme.colors.text.primary};
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${(props) => props.theme.fonts.primary};

  &:hover {
    transform: translateY(-1px);
    background: ${(props) =>
      props.primary
        ? '#E03E0C'
        : props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const WalkUpText = styled.div`
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'dark' ? '#EF4444' : '#7F1D1D')};
`;

// Icon components
const EditIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);

const TrashIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

// Ticket Type Management Styles
const AddTicketButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  border: 2px dashed ${(props) => props.theme.colors.primary};
  border-radius: 8px;
  color: ${(props) => props.theme.colors.primary};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(250, 70, 22, 0.15)'
        : 'rgba(250, 70, 22, 0.1)'};
    transform: translateY(-2px);
  }
`;

const TicketTypeCard = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid ${(props) => props.theme.colors.card.border};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.06)'
        : 'rgba(0, 0, 0, 0.04)'};
  }
`;

const TicketTypeInfo = styled.div`
  flex: 1;
`;

const TicketTypeName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin-bottom: 4px;
`;

const TicketTypeDetails = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.colors.textGray.muted};
`;

const TicketTypeActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.card.border};
  background: ${(props) => props.theme.colors.card.background};
  color: ${(props) => props.theme.colors.text.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => props.theme.colors.interactive.hoverBackground};
    transform: translateY(-1px);
  }
`;

// Modal-specific styled components
const ModalFormGrid = styled.div`
  display: grid;
  gap: 20px;
`;

const ModalFormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  > * {
    flex: 0 0 calc(50% - 8px);
  }
`;

const ModalButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: ${(props) => props.theme.fonts.primary};

  ${(props) =>
    props.primary
      ? `
    background: ${props.theme.colors.primary};
    color: #ffffff;
    border: none;

    &:hover {
      background: #E03E0C;
      transform: translateY(-1px);
    }
  `
      : `
    background: transparent;
    color: ${props.theme.colors.text.primary};
    border: 1px solid ${props.theme.colors.card.border};

    &:hover {
      background: ${
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.05)'
      };
    }
  `}
`;

// Scenario tag components
const ScenarioTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  margin-bottom: 24px;
`;

const ScenarioTag = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.04)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.15)'};
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => props.theme.colors.text.primary};
  opacity: ${(props) => (props.locked ? 0.6 : props.disabled ? 0.4 : 1)};
  cursor: ${(props) => (props.locked ? 'not-allowed' : 'default')};
  transition: opacity 0.2s;
`;

const RemoveScenarioButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.text.primary};
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  opacity: 0.7;
  padding: 0;
  margin: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const AddScenarioButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  opacity: 0.8;
  padding: 0;
  margin: 0;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const LockIcon = styled.svg`
  width: 14px;
  height: 14px;
  opacity: 0.5;
`;

const ScenarioSelectorLabel = styled.label`
  font-size: 12px;
  color: ${(props) => props.theme.colors.textGray.muted};
  margin-bottom: 6px;
  display: block;
`;

// Wizard-specific styled components
const WizardStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const WizardStepTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0 0 12px 0;
`;

const WizardStepDescription = styled.p`
  font-size: 14px;
  color: ${(props) => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
`;

const WizardPlatformSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
`;

const WizardProgress = styled.div`
  font-size: 13px;
  color: ${(props) => props.theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 16px;
  font-weight: 500;
`;

const WizardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  margin: 0;
`;

const WizardButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const WizardSkipButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-right: 20px;
  font-family: ${(props) => props.theme.fonts.primary};

  &:hover {
    color: ${(props) => props.theme.colors.text.primary};
  }
`;

// Platform configurations
const platforms = {
  tickethoss: {
    name: 'TicketHoss',
    feeRange: '$0.75 - $1.75 fees',
    items: {
      general_admission: {
        name: 'General Admission',
        type: 'price_tiered',
        threshold: 5,
        under: { fee: 0.75, type: 'fixed' },
        over: { fee: 1.25, type: 'fixed' },
      },
      reserved_seating: {
        name: 'Reserved Seating',
        type: 'price_tiered',
        threshold: 5,
        under: { fee: 1.25, type: 'fixed' },
        over: { fee: 1.75, type: 'fixed' },
      },
      non_admission: {
        name: 'Non-Admission Items',
        fee: 0.02,
        type: 'percentage',
      },
      suite_bundles: { name: 'Suite Ticket Bundles', fee: 0, type: 'custom' },
      lap_sponsorship: {
        name: 'LAP Sponsorship',
        fee: 0.02,
        type: 'percentage',
      },
      season_tickets: { name: 'Season Tickets', fee: 0, type: 'custom' },
    },
  },
  pitpay: {
    name: 'Pit Pay',
    feeRange: '$1.85 - $10 fees',
    items: {
      pit_pass: { name: 'Pit Pass', fee: 1.85, type: 'fixed' },
      registration: {
        name: 'Registration',
        type: 'price_tiered',
        threshold: 500,
        under: { fee: 0.02, type: 'percentage' },
        over: { fee: 10, type: 'fixed' },
      },
      memberships: { name: 'Memberships', fee: 4.75, type: 'fixed' },
      other_items: { name: 'Other Items', fee: 0.02, type: 'percentage' },
      full_season_pit_pass: {
        name: 'Full Season Pit Pass',
        fee: 0,
        type: 'custom',
      },
      reserved_from_map: { name: 'Reserved from Map', fee: 0, type: 'custom' },
    },
  },
};

const PricingCalculator = () => {
  const history = useHistory();
  const location = useLocation();

  // Determine route prefix based on current path
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  // State
  const [selectedPlatform, setSelectedPlatform] = useState('tickethoss');
  const [ticketTypes, setTicketTypes] = useState([
    {
      id: 1,
      name: 'Adult General Admission',
      quantity: 100,
      prices: {
        earlyBird: 12,
        advance: 15,
        dayOfApp: 18,
        walkUp: 20,
      },
      enabledScenarios: {
        earlyBird: true,
        advance: true,
        dayOfApp: true,
        walkUp: true, // Always true
      },
      platformType: 'general_admission', // For fee calculation
      platformName: 'tickethoss', // Track which platform this ticket is for
    },
  ]);
  const [earlyBirdPercent, setEarlyBirdPercent] = useState(30);
  const [advancePercent, setAdvancePercent] = useState(40);
  const [dayOfAppPercent, setDayOfAppPercent] = useState(20);
  const [walkUpCardPercent, setWalkUpCardPercent] = useState(20); // Card vs Cash split
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTicketType, setEditingTicketType] = useState(null);

  // Wizard state
  const [isWizardOpen, setIsWizardOpen] = useState(true);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardError, setWizardError] = useState('');
  const [wizardData, setWizardData] = useState({
    platform: 'tickethoss',
    name: '',
    quantity: 100,
    platformType: 'general_admission',
    enabledScenarios: {
      earlyBird: true,
      advance: true,
      dayOfApp: true,
      walkUp: true,
    },
    earlyBirdPrice: '',
    advancePrice: '',
    dayOfAppPrice: '',
    walkUpPrice: '',
  });

  // Modal form state
  const [formData, setFormData] = useState({
    name: '',
    quantity: 100,
    earlyBirdPrice: 12,
    advancePrice: 15,
    dayOfAppPrice: 18,
    walkUpPrice: 20,
    platformType: 'general_admission',
    enabledScenarios: {
      earlyBird: true,
      advance: true,
      dayOfApp: true,
      walkUp: true,
    },
  });

  // Scenario management
  const scenarioLabels = {
    earlyBird: 'Early Bird',
    advance: 'Advance',
    dayOfApp: 'Day-Of App',
    walkUp: 'Walk-Up',
  };

  const toggleScenario = (scenarioKey) => {
    if (scenarioKey === 'walkUp') return; // Walk-up is always required

    setFormData({
      ...formData,
      enabledScenarios: {
        ...formData.enabledScenarios,
        [scenarioKey]: !formData.enabledScenarios[scenarioKey],
      },
    });
  };

  // Calculate walk-up percent automatically
  const walkUpPercent =
    100 - earlyBirdPercent - advancePercent - dayOfAppPercent;
  const walkUpCashPercent = 100 - walkUpCardPercent;

  // Calculate total tickets across all ticket types
  const totalTickets = ticketTypes.reduce(
    (sum, type) => sum + type.quantity,
    0
  );

  // Calculate ticket counts
  const earlyBirdTickets = Math.round((totalTickets * earlyBirdPercent) / 100);
  const advanceTickets = Math.round((totalTickets * advancePercent) / 100);
  const dayOfAppTickets = Math.round((totalTickets * dayOfAppPercent) / 100);
  const walkUpTickets =
    totalTickets - earlyBirdTickets - advanceTickets - dayOfAppTickets;

  // Split walk-up into card and cash
  const walkUpCardTickets = Math.round(
    (walkUpTickets * walkUpCardPercent) / 100
  );
  const walkUpCashTickets = walkUpTickets - walkUpCardTickets;

  // Helper function to map old platformType values to new base types for backward compatibility
  const normalizePlatformType = (platformType) => {
    const mapping = {
      general_over5: 'general_admission',
      general_under5: 'general_admission',
      reserved_over5: 'reserved_seating',
      reserved_under5: 'reserved_seating',
      registration_under500: 'registration',
      registration_over500: 'registration',
    };
    return mapping[platformType] || platformType;
  };

  // Helper function to detect if a ticket type is reserved seating
  const isReservedSeating = (platformType) => {
    const normalized = normalizePlatformType(platformType);
    return normalized === 'reserved_seating';
  };

  // Calculate fees for a specific ticket type and price
  const calculateFee = (price, platformType) => {
    const platform = platforms[selectedPlatform];
    // Normalize old platformType values for backward compatibility
    const normalizedType = normalizePlatformType(platformType);
    const item = platform.items[normalizedType];

    if (!item) return 0;

    // Handle price-tiered fee structures
    if (item.type === 'price_tiered') {
      const tier = price <= item.threshold ? item.under : item.over;
      if (tier.type === 'fixed') {
        return tier.fee;
      } else if (tier.type === 'percentage') {
        return price * tier.fee;
      }
      return 0;
    }

    // Handle standard fee types
    if (item.type === 'fixed') {
      return item.fee;
    } else if (item.type === 'percentage') {
      return price * item.fee;
    } else if (item.type === 'percentage_capped') {
      const percentFee = price * item.fee;
      return Math.min(percentFee, item.cap || 10);
    }
    return 0;
  };

  // Aggregate calculations across all ticket types
  let totalRevenue = 0;
  let totalPlatformFees = 0;
  let earlyBirdRevenue = 0;
  let advanceRevenue = 0;
  let dayOfAppRevenue = 0;
  let walkUpCardRevenue = 0;
  let walkUpCashRevenue = 0;

  ticketTypes.forEach((ticketType) => {
    const typeEarlyBirdTickets = Math.round(
      (ticketType.quantity * earlyBirdPercent) / 100
    );
    const typeAdvanceTickets = Math.round(
      (ticketType.quantity * advancePercent) / 100
    );
    const typeDayOfAppTickets = Math.round(
      (ticketType.quantity * dayOfAppPercent) / 100
    );
    const typeWalkUpTickets =
      ticketType.quantity -
      typeEarlyBirdTickets -
      typeAdvanceTickets -
      typeDayOfAppTickets;

    // Split walk-up into card and cash for this ticket type
    const typeWalkUpCardTickets = Math.round(
      (typeWalkUpTickets * walkUpCardPercent) / 100
    );
    const typeWalkUpCashTickets = typeWalkUpTickets - typeWalkUpCardTickets;

    // Calculate revenue for this ticket type
    const typeEarlyBirdRev = typeEarlyBirdTickets * ticketType.prices.earlyBird;
    const typeAdvanceRev = typeAdvanceTickets * ticketType.prices.advance;
    const typeDayOfAppRev = typeDayOfAppTickets * ticketType.prices.dayOfApp;
    const typeWalkUpCardRev = typeWalkUpCardTickets * ticketType.prices.walkUp;
    const typeWalkUpCashRev = typeWalkUpCashTickets * ticketType.prices.walkUp;

    earlyBirdRevenue += typeEarlyBirdRev;
    advanceRevenue += typeAdvanceRev;
    dayOfAppRevenue += typeDayOfAppRev;
    walkUpCardRevenue += typeWalkUpCardRev;
    walkUpCashRevenue += typeWalkUpCashRev;

    // Calculate fees for this ticket type
    const typeEarlyBirdFees =
      typeEarlyBirdTickets *
      calculateFee(ticketType.prices.earlyBird, ticketType.platformType);
    const typeAdvanceFees =
      typeAdvanceTickets *
      calculateFee(ticketType.prices.advance, ticketType.platformType);
    const typeDayOfAppFees =
      typeDayOfAppTickets *
      calculateFee(ticketType.prices.dayOfApp, ticketType.platformType);

    // Walk-up card: 2% platform fee
    const typeWalkUpCardFees =
      typeWalkUpCardTickets * ticketType.prices.walkUp * 0.02;

    // Walk-up cash: $0 for general admission, $1.25 for reserved seating
    const typeWalkUpCashFees = isReservedSeating(ticketType.platformType)
      ? typeWalkUpCashTickets * 1.25
      : 0;

    totalPlatformFees +=
      typeEarlyBirdFees +
      typeAdvanceFees +
      typeDayOfAppFees +
      typeWalkUpCardFees +
      typeWalkUpCashFees;
  });

  const walkUpRevenue = walkUpCardRevenue + walkUpCashRevenue;
  totalRevenue =
    earlyBirdRevenue + advanceRevenue + dayOfAppRevenue + walkUpRevenue;

  // Processing fees: 3.25% + $0.30 per transaction
  // Walk-up card transactions get processing fees, cash transactions don't
  const nonWalkUpTickets = earlyBirdTickets + advanceTickets + dayOfAppTickets;
  const totalProcessingFees =
    (nonWalkUpTickets + walkUpCardTickets) * 0.3 +
    (earlyBirdRevenue + advanceRevenue + dayOfAppRevenue + walkUpCardRevenue) *
      0.0325;

  const netRevenue = totalRevenue - totalPlatformFees - totalProcessingFees;

  // Savings calculations (using average prices across all ticket types)
  const avgEarlyBirdPrice =
    ticketTypes.reduce((sum, t) => sum + t.prices.earlyBird, 0) /
    (ticketTypes.length || 1);
  const avgAdvancePrice =
    ticketTypes.reduce((sum, t) => sum + t.prices.advance, 0) /
    (ticketTypes.length || 1);
  const avgWalkUpPrice =
    ticketTypes.reduce((sum, t) => sum + t.prices.walkUp, 0) /
    (ticketTypes.length || 1);

  const earlyBirdSavings = avgWalkUpPrice - avgEarlyBirdPrice;
  const earlyBirdSavingsPercent = Math.round(
    (earlyBirdSavings / avgWalkUpPrice) * 100
  );
  const advanceSavings = avgWalkUpPrice - avgAdvancePrice;
  const advanceSavingsPercent = Math.round(
    (advanceSavings / avgWalkUpPrice) * 100
  );

  const handleBack = () => {
    history.goBack();
  };

  const handleComplete = () => {
    history.push(`${routePrefix}/home`);
  };

  const handleReset = () => {
    setSelectedPlatform('tickethoss');
    setTicketTypes([
      {
        id: 1,
        name: 'Adult General Admission',
        quantity: 100,
        prices: {
          earlyBird: 12,
          advance: 15,
          dayOfApp: 18,
          walkUp: 20,
        },
        platformType: 'general_admission',
      },
    ]);
    setEarlyBirdPercent(30);
    setAdvancePercent(40);
    setDayOfAppPercent(20);
    setWalkUpCardPercent(20);
  };

  const handleAddTicketType = () => {
    setFormData({
      name: '',
      quantity: 100,
      earlyBirdPrice: 12,
      advancePrice: 15,
      dayOfAppPrice: 18,
      walkUpPrice: 20,
      platformType: 'general_admission',
      enabledScenarios: {
        earlyBird: true,
        advance: true,
        dayOfApp: true,
        walkUp: true,
      },
    });
    setEditingTicketType(null);
    setIsModalOpen(true);
  };

  const handleEditTicketType = (ticketType) => {
    setFormData({
      name: ticketType.name,
      quantity: ticketType.quantity,
      earlyBirdPrice: ticketType.prices.earlyBird,
      advancePrice: ticketType.prices.advance,
      dayOfAppPrice: ticketType.prices.dayOfApp,
      walkUpPrice: ticketType.prices.walkUp,
      platformType: ticketType.platformType,
      enabledScenarios: ticketType.enabledScenarios || {
        earlyBird: true,
        advance: true,
        dayOfApp: true,
        walkUp: true,
      },
    });
    setEditingTicketType(ticketType);
    setIsModalOpen(true);
  };

  const handleDeleteTicketType = (id) => {
    if (ticketTypes.length === 1) {
      alert('You must have at least one ticket type.');
      return;
    }
    setTicketTypes(ticketTypes.filter((t) => t.id !== id));
  };

  const handleSaveTicketType = () => {
    if (!formData.name.trim()) {
      alert('Please enter a ticket type name.');
      return;
    }

    if (editingTicketType) {
      // Update existing
      setTicketTypes(
        ticketTypes.map((t) =>
          t.id === editingTicketType.id
            ? {
                ...t,
                name: formData.name,
                quantity: formData.quantity,
                prices: {
                  earlyBird: formData.earlyBirdPrice,
                  advance: formData.advancePrice,
                  dayOfApp: formData.dayOfAppPrice,
                  walkUp: formData.walkUpPrice,
                },
                enabledScenarios: formData.enabledScenarios,
                platformType: formData.platformType,
                platformName: selectedPlatform,
              }
            : t
        )
      );
    } else {
      // Add new
      const newId =
        ticketTypes.length > 0
          ? Math.max(...ticketTypes.map((t) => t.id)) + 1
          : 1;
      setTicketTypes([
        ...ticketTypes,
        {
          id: newId,
          name: formData.name,
          quantity: formData.quantity,
          prices: {
            earlyBird: formData.earlyBirdPrice,
            advance: formData.advancePrice,
            dayOfApp: formData.dayOfAppPrice,
            walkUp: formData.walkUpPrice,
          },
          enabledScenarios: formData.enabledScenarios,
          platformType: formData.platformType,
          platformName: selectedPlatform,
        },
      ]);
    }

    setIsModalOpen(false);
    setEditingTicketType(null);
  };

  const handleCancelModal = () => {
    setIsModalOpen(false);
    setEditingTicketType(null);
  };

  const handleExport = () => {
    alert(
      'Export functionality would generate a PDF report with these calculations'
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Wizard handlers
  const getTotalWizardSteps = () => {
    return 6; // Platform, Name, Quantity, Fee Type, Scenarios, Prices
  };

  const validateWizardStep = () => {
    const currentStepNumber = wizardStep;

    // Step 1: Platform (always valid, one is always selected)
    if (currentStepNumber === 1) {
      return true;
    }

    // Step 2: Ticket Name
    if (currentStepNumber === 2) {
      if (!wizardData.name.trim()) {
        setWizardError('Please enter a ticket name');
        return false;
      }
      return true;
    }

    // Step 3: Quantity
    if (currentStepNumber === 3) {
      if (!wizardData.quantity || wizardData.quantity <= 0) {
        setWizardError('Please enter a quantity greater than 0');
        return false;
      }
      return true;
    }

    // Step 4: Platform Fee Type (always valid, one is always selected)
    if (currentStepNumber === 4) {
      return true;
    }

    // Step 5: Pricing Scenarios (at least walk-up is always enabled)
    if (currentStepNumber === 5) {
      return true;
    }

    // Step 6: Prices - validate all enabled scenario prices
    if (currentStepNumber === 6) {
      // Validate early bird if enabled
      if (wizardData.enabledScenarios.earlyBird) {
        const price = parseFloat(wizardData.earlyBirdPrice);
        if (!wizardData.earlyBirdPrice || isNaN(price) || price <= 0) {
          setWizardError('Please enter an early bird price greater than $0');
          return false;
        }
      }

      // Validate advance if enabled
      if (wizardData.enabledScenarios.advance) {
        const price = parseFloat(wizardData.advancePrice);
        if (!wizardData.advancePrice || isNaN(price) || price <= 0) {
          setWizardError('Please enter an advance price greater than $0');
          return false;
        }
      }

      // Validate day-of if enabled
      if (wizardData.enabledScenarios.dayOfApp) {
        const price = parseFloat(wizardData.dayOfAppPrice);
        if (!wizardData.dayOfAppPrice || isNaN(price) || price <= 0) {
          setWizardError('Please enter a day-of price greater than $0');
          return false;
        }
      }

      // Always validate walk-up (always required)
      const walkUpPrice = parseFloat(wizardData.walkUpPrice);
      if (!wizardData.walkUpPrice || isNaN(walkUpPrice) || walkUpPrice <= 0) {
        setWizardError('Please enter a walk-up price greater than $0');
        return false;
      }

      return true;
    }

    return true;
  };

  const handleWizardNext = () => {
    // Clear any previous errors
    setWizardError('');

    // Validate current step
    if (!validateWizardStep()) {
      return;
    }

    const totalSteps = getTotalWizardSteps();
    if (wizardStep < totalSteps) {
      setWizardStep(wizardStep + 1);
    } else {
      handleWizardComplete();
    }
  };

  const handleWizardBack = () => {
    setWizardError(''); // Clear errors when going back
    if (wizardStep > 1) {
      setWizardStep(wizardStep - 1);
    }
  };

  const handleWizardSkip = () => {
    setIsWizardOpen(false);
    setWizardCompleted(true);
  };

  const handleWizardComplete = () => {
    // Create the first ticket with wizard data
    const newTicket = {
      id: 1,
      name: wizardData.name || 'My First Ticket',
      quantity: wizardData.quantity,
      prices: {
        earlyBird: parseFloat(wizardData.earlyBirdPrice) || 0,
        advance: parseFloat(wizardData.advancePrice) || 0,
        dayOfApp: parseFloat(wizardData.dayOfAppPrice) || 0,
        walkUp: parseFloat(wizardData.walkUpPrice) || 0,
      },
      enabledScenarios: wizardData.enabledScenarios,
      platformType: wizardData.platformType,
      platformName: wizardData.platform,
    };

    setTicketTypes([newTicket]);
    setSelectedPlatform(wizardData.platform);

    // Adjust percentages based on enabled scenarios
    // Count how many scenarios are enabled (excluding walk-up which is calculated)
    const enabledCount =
      (wizardData.enabledScenarios.earlyBird ? 1 : 0) +
      (wizardData.enabledScenarios.advance ? 1 : 0) +
      (wizardData.enabledScenarios.dayOfApp ? 1 : 0);

    // Distribute percentages evenly among enabled scenarios
    // Reserve 40% for walk-up, distribute remaining 60% among enabled scenarios
    const percentPerScenario =
      enabledCount > 0 ? Math.floor(60 / enabledCount) : 0;

    if (wizardData.enabledScenarios.earlyBird) {
      setEarlyBirdPercent(percentPerScenario);
    } else {
      setEarlyBirdPercent(0);
    }

    if (wizardData.enabledScenarios.advance) {
      setAdvancePercent(percentPerScenario);
    } else {
      setAdvancePercent(0);
    }

    if (wizardData.enabledScenarios.dayOfApp) {
      setDayOfAppPercent(percentPerScenario);
    } else {
      setDayOfAppPercent(0);
    }

    setIsWizardOpen(false);
    setWizardCompleted(true);
  };

  const toggleWizardScenario = (scenarioKey) => {
    if (scenarioKey === 'walkUp') return; // Walk-up is always required

    setWizardData({
      ...wizardData,
      enabledScenarios: {
        ...wizardData.enabledScenarios,
        [scenarioKey]: !wizardData.enabledScenarios[scenarioKey],
      },
    });
  };

  // Check which scenarios are enabled across all ticket types
  const globalEnabledScenarios = {
    earlyBird: ticketTypes.some((t) => t.enabledScenarios?.earlyBird),
    advance: ticketTypes.some((t) => t.enabledScenarios?.advance),
    dayOfApp: ticketTypes.some((t) => t.enabledScenarios?.dayOfApp),
    walkUp: true, // Always enabled
  };

  // Generate ticket type options dynamically based on selected platform
  const ticketTypeOptions = Object.entries(
    platforms[selectedPlatform].items
  ).map(([key, item]) => ({
    value: key,
    label: item.name,
  }));

  // Generate wizard ticket type options based on wizard platform selection
  const wizardTicketTypeOptions = Object.entries(
    platforms[wizardData.platform].items
  ).map(([key, item]) => ({
    value: key,
    label: item.name,
  }));

  // Render wizard step content
  const renderWizardStep = () => {
    const currentStepNumber = wizardStep;

    // Step 1: Platform Selection
    if (currentStepNumber === 1) {
      return (
        <WizardStepContainer>
          <div>
            <WizardStepTitle>Choose Your Platform</WizardStepTitle>
            <WizardStepDescription>
              Select whether you're creating tickets for Ticket Hoss or Pit Pay.
              Each platform has different fee structures and ticket types
              optimized for specific use cases.
            </WizardStepDescription>
          </div>
          <WizardPlatformSelector>
            <PlatformOption
              selected={wizardData.platform === 'tickethoss'}
              onClick={() => {
                setWizardData({
                  ...wizardData,
                  platform: 'tickethoss',
                  platformType: 'general_admission',
                });
              }}
            >
              <PlatformIcon selected={wizardData.platform === 'tickethoss'}>
                TH
              </PlatformIcon>
              <PlatformInfo>
                <PlatformName selected={wizardData.platform === 'tickethoss'}>
                  TicketHoss
                </PlatformName>
                <PlatformFees>{platforms.tickethoss.feeRange}</PlatformFees>
              </PlatformInfo>
            </PlatformOption>
            <PlatformOption
              selected={wizardData.platform === 'pitpay'}
              onClick={() => {
                setWizardData({
                  ...wizardData,
                  platform: 'pitpay',
                  platformType: 'pit_pass',
                });
              }}
            >
              <PlatformIcon selected={wizardData.platform === 'pitpay'}>
                PP
              </PlatformIcon>
              <PlatformInfo>
                <PlatformName selected={wizardData.platform === 'pitpay'}>
                  Pit Pay
                </PlatformName>
                <PlatformFees>{platforms.pitpay.feeRange}</PlatformFees>
              </PlatformInfo>
            </PlatformOption>
          </WizardPlatformSelector>
        </WizardStepContainer>
      );
    }

    // Step 2: Ticket Name
    if (currentStepNumber === 2) {
      return (
        <WizardStepContainer>
          <div>
            <WizardStepTitle>Name Your Ticket</WizardStepTitle>
            <WizardStepDescription>
              Give this ticket a descriptive name like "Adult General
              Admission", "VIP Pass", or "Student Ticket". This helps you and
              your customers identify the ticket type.
            </WizardStepDescription>
          </div>
          <Input
            label="Ticket Name"
            type="text"
            value={wizardData.name}
            onChange={(e) =>
              setWizardData({ ...wizardData, name: e.target.value })
            }
            placeholder="e.g. Adult General Admission, VIP, Student"
            error={!!wizardError}
            helperText={wizardError}
          />
        </WizardStepContainer>
      );
    }

    // Step 3: Quantity
    if (currentStepNumber === 3) {
      return (
        <WizardStepContainer>
          <div>
            <WizardStepTitle>Set Expected Quantity</WizardStepTitle>
            <WizardStepDescription>
              Enter the total number of tickets you expect to sell for this
              type. This helps calculate your revenue projections. You can
              always adjust this later.
            </WizardStepDescription>
          </div>
          <Input
            label="Expected Quantity"
            type="number"
            value={String(wizardData.quantity)}
            onChange={(e) =>
              setWizardData({
                ...wizardData,
                quantity: parseInt(e.target.value) || 0,
              })
            }
            min="1"
            error={!!wizardError}
            helperText={wizardError}
          />
        </WizardStepContainer>
      );
    }

    // Step 4: Platform Fee Type
    if (currentStepNumber === 4) {
      return (
        <WizardStepContainer>
          <div>
            <WizardStepTitle>Select Fee Structure</WizardStepTitle>
            <WizardStepDescription>
              Our system has different fee structures for certain types of
              tickets at certain price points. Choose the category that best
              matches this ticket type to ensure accurate fee calculations.
            </WizardStepDescription>
          </div>
          <Select
            label="Platform Fee Type"
            value={wizardData.platformType}
            onChange={(e) =>
              setWizardData({ ...wizardData, platformType: e.target.value })
            }
            options={wizardTicketTypeOptions}
          />
        </WizardStepContainer>
      );
    }

    // Step 5: Pricing Scenarios selection
    if (currentStepNumber === 5) {
      return (
        <WizardStepContainer>
          <div>
            <WizardStepTitle>Choose Pricing Scenarios</WizardStepTitle>
            <WizardStepDescription>
              Select which pricing options you want to offer. Early bird pricing
              encourages advance purchases, while day-of pricing gives
              flexibility. Walk-up pricing is always required for gate sales.
            </WizardStepDescription>
          </div>
          <div>
            <ScenarioSelectorLabel>
              Select pricing options to enable
            </ScenarioSelectorLabel>
            <ScenarioTagsContainer>
              {Object.entries(scenarioLabels).map(([key, label]) => (
                <ScenarioTag
                  key={key}
                  locked={key === 'walkUp'}
                  disabled={
                    !wizardData.enabledScenarios[key] && key !== 'walkUp'
                  }
                >
                  {label}
                  {key === 'walkUp' ? (
                    <LockIcon viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </LockIcon>
                  ) : wizardData.enabledScenarios[key] ? (
                    <RemoveScenarioButton
                      onClick={() => toggleWizardScenario(key)}
                    >
                      ×
                    </RemoveScenarioButton>
                  ) : (
                    <AddScenarioButton
                      onClick={() => toggleWizardScenario(key)}
                    >
                      +
                    </AddScenarioButton>
                  )}
                </ScenarioTag>
              ))}
            </ScenarioTagsContainer>
          </div>
        </WizardStepContainer>
      );
    }

    // Step 6: Set all prices for selected scenarios
    if (currentStepNumber === 6) {
      return (
        <WizardStepContainer>
          <div>
            <WizardStepTitle>Set Your Prices</WizardStepTitle>
            <WizardStepDescription>
              Enter the price for each pricing option you selected. These prices
              help calculate your revenue projections.
            </WizardStepDescription>
          </div>

          {/* Price Input Fields */}
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            {wizardData.enabledScenarios.earlyBird && (
              <Input
                label="Early Bird Price"
                type="number"
                step="0.25"
                value={wizardData.earlyBirdPrice}
                onChange={(e) =>
                  setWizardData({
                    ...wizardData,
                    earlyBirdPrice: e.target.value,
                  })
                }
                error={
                  !!wizardError &&
                  (!wizardData.earlyBirdPrice ||
                    parseFloat(wizardData.earlyBirdPrice) <= 0)
                }
                helperText="Discounted price for early purchases"
              />
            )}

            {wizardData.enabledScenarios.advance && (
              <Input
                label="Advance Price"
                type="number"
                step="0.25"
                value={wizardData.advancePrice}
                onChange={(e) =>
                  setWizardData({
                    ...wizardData,
                    advancePrice: e.target.value,
                  })
                }
                error={
                  !!wizardError &&
                  (!wizardData.advancePrice ||
                    parseFloat(wizardData.advancePrice) <= 0)
                }
                helperText="Standard pre-event price"
              />
            )}

            {wizardData.enabledScenarios.dayOfApp && (
              <Input
                label="Day-Of App Price"
                type="number"
                step="0.25"
                value={wizardData.dayOfAppPrice}
                onChange={(e) =>
                  setWizardData({
                    ...wizardData,
                    dayOfAppPrice: e.target.value,
                  })
                }
                error={
                  !!wizardError &&
                  (!wizardData.dayOfAppPrice ||
                    parseFloat(wizardData.dayOfAppPrice) <= 0)
                }
                helperText="Price for mobile purchases on event day"
              />
            )}

            <Input
              label="Walk-Up Price"
              type="number"
              step="0.25"
              value={wizardData.walkUpPrice}
              onChange={(e) =>
                setWizardData({
                  ...wizardData,
                  walkUpPrice: e.target.value,
                })
              }
              error={
                !!wizardError &&
                (!wizardData.walkUpPrice ||
                  parseFloat(wizardData.walkUpPrice) <= 0)
              }
              helperText="Gate price on event day (always required)"
            />
          </div>
        </WizardStepContainer>
      );
    }

    return null;
  };

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        <ContentWrapper>
          <Title>Revenue Calculator</Title>
          <Subtitle>
            Calculate your revenue scenarios with different pricing strategies.
            See how early bird discounts and pricing tiers affect your bottom
            line.
          </Subtitle>

          <DashboardLayout>
            <div>
              <CardWrapper>
                <Card title="Platform & Ticket Configuration" compact>
                  <PlatformSelector>
                    <PlatformOption
                      selected={selectedPlatform === 'tickethoss'}
                      onClick={() => {
                        setSelectedPlatform('tickethoss');
                        // Reset ticket types to use general_admission for TicketHoss
                        setTicketTypes(
                          ticketTypes.map((t) => ({
                            ...t,
                            platformType: 'general_admission',
                            platformName: 'tickethoss',
                            name: t.name.replace(
                              'Pit Pass',
                              'General Admission'
                            ),
                          }))
                        );
                      }}
                    >
                      <PlatformIcon
                        selected={selectedPlatform === 'tickethoss'}
                      >
                        TH
                      </PlatformIcon>
                      <PlatformInfo>
                        <PlatformName
                          selected={selectedPlatform === 'tickethoss'}
                        >
                          TicketHoss
                        </PlatformName>
                        <PlatformFees>
                          {platforms.tickethoss.feeRange}
                        </PlatformFees>
                      </PlatformInfo>
                    </PlatformOption>
                    <PlatformOption
                      selected={selectedPlatform === 'pitpay'}
                      onClick={() => {
                        setSelectedPlatform('pitpay');
                        // Reset ticket types to use pit_pass for Pit Pay
                        setTicketTypes(
                          ticketTypes.map((t) => ({
                            ...t,
                            platformType: 'pit_pass',
                            platformName: 'pitpay',
                            name: t.name.replace(
                              'General Admission',
                              'Pit Pass'
                            ),
                          }))
                        );
                      }}
                    >
                      <PlatformIcon selected={selectedPlatform === 'pitpay'}>
                        PP
                      </PlatformIcon>
                      <PlatformInfo>
                        <PlatformName selected={selectedPlatform === 'pitpay'}>
                          Pit Pay
                        </PlatformName>
                        <PlatformFees>{platforms.pitpay.feeRange}</PlatformFees>
                      </PlatformInfo>
                    </PlatformOption>
                  </PlatformSelector>

                  <CardContent>
                    <SectionTitle>Ticket Types</SectionTitle>
                    {ticketTypes.map((ticketType) => (
                      <TicketTypeCard key={ticketType.id}>
                        <TicketTypeInfo>
                          <TicketTypeName>{ticketType.name}</TicketTypeName>
                          <TicketTypeDetails>
                            {ticketType.quantity} tickets •{' '}
                            {formatCurrency(ticketType.prices.earlyBird)} -{' '}
                            {formatCurrency(ticketType.prices.walkUp)}
                          </TicketTypeDetails>
                        </TicketTypeInfo>
                        <TicketTypeActions>
                          <IconButton
                            onClick={() => handleEditTicketType(ticketType)}
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              handleDeleteTicketType(ticketType.id)
                            }
                            title="Delete"
                          >
                            <TrashIcon />
                          </IconButton>
                        </TicketTypeActions>
                      </TicketTypeCard>
                    ))}
                    <AddTicketButton onClick={handleAddTicketType}>
                      + Add Ticket Type
                    </AddTicketButton>
                  </CardContent>
                </Card>
              </CardWrapper>

              <CardWrapper>
                <Card title="Sales Distribution Projections" compact>
                  <SliderGrid>
                    {globalEnabledScenarios.earlyBird && (
                      <SliderCard accentColor="#3B82F6">
                        <SliderHeader>
                          <SliderLabel>Early Bird Sales</SliderLabel>
                          <SliderValue color="#3B82F6">
                            {earlyBirdPercent}%
                          </SliderValue>
                        </SliderHeader>
                        <Slider
                          type="range"
                          min="0"
                          max="70"
                          value={earlyBirdPercent}
                          onChange={(e) =>
                            setEarlyBirdPercent(parseInt(e.target.value))
                          }
                          step="5"
                          thumbColor="#3B82F6"
                        />
                        <HelpText>Early bird discount</HelpText>
                      </SliderCard>
                    )}

                    {globalEnabledScenarios.advance && (
                      <SliderCard accentColor="#22C55E">
                        <SliderHeader>
                          <SliderLabel>Advance Sales</SliderLabel>
                          <SliderValue color="#22C55E">
                            {advancePercent}%
                          </SliderValue>
                        </SliderHeader>
                        <Slider
                          type="range"
                          min="0"
                          max="70"
                          value={advancePercent}
                          onChange={(e) =>
                            setAdvancePercent(parseInt(e.target.value))
                          }
                          step="5"
                          thumbColor="#22C55E"
                        />
                        <HelpText>Regular advance purchases</HelpText>
                      </SliderCard>
                    )}

                    {globalEnabledScenarios.dayOfApp && (
                      <SliderCard accentColor="#F59E0B">
                        <SliderHeader>
                          <SliderLabel>In-App Day-Of</SliderLabel>
                          <SliderValue color="#F59E0B">
                            {dayOfAppPercent}%
                          </SliderValue>
                        </SliderHeader>
                        <Slider
                          type="range"
                          min="0"
                          max="50"
                          value={dayOfAppPercent}
                          onChange={(e) =>
                            setDayOfAppPercent(parseInt(e.target.value))
                          }
                          step="5"
                          thumbColor="#F59E0B"
                        />
                        <HelpText>Mobile app purchases</HelpText>
                      </SliderCard>
                    )}

                    <WalkUpCard>
                      <SliderLabel>Walk-Up</SliderLabel>
                      <WalkUpValue>{walkUpPercent}%</WalkUpValue>
                      <WalkUpText>
                        Automatically calculated (remaining %)
                      </WalkUpText>

                      <div
                        style={{
                          marginTop: '20px',
                          paddingTop: '16px',
                          borderTop: '1px solid rgba(239, 68, 68, 0.2)',
                        }}
                      >
                        <SliderLabel style={{ marginBottom: '12px' }}>
                          Payment Method Split
                        </SliderLabel>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '8px',
                          }}
                        >
                          <span style={{ fontSize: '13px', color: '#EF4444' }}>
                            Card: {walkUpCardPercent}%
                          </span>
                          <span style={{ fontSize: '13px', color: '#EF4444' }}>
                            Cash: {walkUpCashPercent}%
                          </span>
                        </div>
                        <Slider
                          type="range"
                          min="0"
                          max="100"
                          value={walkUpCardPercent}
                          onChange={(e) =>
                            setWalkUpCardPercent(parseInt(e.target.value))
                          }
                          step="5"
                          thumbColor="#EF4444"
                        />
                        <HelpText>
                          Percentage of walk-up sales paid by card
                        </HelpText>
                      </div>
                    </WalkUpCard>
                  </SliderGrid>

                  <TicketCountGrid>
                    {globalEnabledScenarios.earlyBird && (
                      <TicketCountCard
                        bg="rgba(59, 130, 246, 0.08)"
                        borderColor="rgba(59, 130, 246, 0.3)"
                      >
                        <TicketCount color="#3B82F6">
                          {earlyBirdTickets}
                        </TicketCount>
                        <TicketLabel>Early Bird</TicketLabel>
                      </TicketCountCard>
                    )}
                    {globalEnabledScenarios.advance && (
                      <TicketCountCard
                        bg="rgba(34, 197, 94, 0.08)"
                        borderColor="rgba(34, 197, 94, 0.3)"
                      >
                        <TicketCount color="#22C55E">
                          {advanceTickets}
                        </TicketCount>
                        <TicketLabel>Advance</TicketLabel>
                      </TicketCountCard>
                    )}
                    {globalEnabledScenarios.dayOfApp && (
                      <TicketCountCard
                        bg="rgba(245, 158, 11, 0.08)"
                        borderColor="rgba(245, 158, 11, 0.3)"
                      >
                        <TicketCount color="#F59E0B">
                          {dayOfAppTickets}
                        </TicketCount>
                        <TicketLabel>Day-Of App</TicketLabel>
                      </TicketCountCard>
                    )}
                    <TicketCountCard
                      bg="rgba(239, 68, 68, 0.08)"
                      borderColor="rgba(239, 68, 68, 0.3)"
                    >
                      <TicketCount color="#EF4444">{walkUpTickets}</TicketCount>
                      <TicketLabel>Walk-Up</TicketLabel>
                    </TicketCountCard>
                  </TicketCountGrid>
                </Card>
              </CardWrapper>
            </div>

            <StickyResults>
              <ResultsPanel>
                <ResultsTitle>Total Revenue Projection</ResultsTitle>
                <InputDescription style={{ marginBottom: '20px' }}>
                  Aggregated across all ticket types
                </InputDescription>

                {globalEnabledScenarios.earlyBird && (
                  <ResultItem accentColor="#3B82F6">
                    <ResultLabel>
                      Early Bird ({earlyBirdTickets} tickets)
                    </ResultLabel>
                    <ResultValue>
                      {formatCurrency(earlyBirdRevenue)}
                    </ResultValue>
                  </ResultItem>
                )}

                {globalEnabledScenarios.advance && (
                  <ResultItem accentColor="#22C55E">
                    <ResultLabel>
                      Advance Sales ({advanceTickets} tickets)
                    </ResultLabel>
                    <ResultValue>{formatCurrency(advanceRevenue)}</ResultValue>
                  </ResultItem>
                )}

                {globalEnabledScenarios.dayOfApp && (
                  <ResultItem accentColor="#F59E0B">
                    <ResultLabel>
                      Day-Of App ({dayOfAppTickets} tickets)
                    </ResultLabel>
                    <ResultValue>{formatCurrency(dayOfAppRevenue)}</ResultValue>
                  </ResultItem>
                )}

                <ResultItem accentColor="#EF4444">
                  <ResultLabel>
                    Walk-Up Card ({walkUpCardTickets} tickets)
                  </ResultLabel>
                  <ResultValue>{formatCurrency(walkUpCardRevenue)}</ResultValue>
                </ResultItem>

                <ResultItem accentColor="#DC2626">
                  <ResultLabel>
                    Walk-Up Cash ({walkUpCashTickets} tickets)
                  </ResultLabel>
                  <ResultValue>{formatCurrency(walkUpCashRevenue)}</ResultValue>
                </ResultItem>

                <ResultItem>
                  <ResultLabel>Processing Fees</ResultLabel>
                  <ResultValue isNegative>
                    -{formatCurrency(totalProcessingFees)}
                  </ResultValue>
                </ResultItem>

                <ResultItem>
                  <ResultLabel>Platform Fees</ResultLabel>
                  <ResultValue isNegative>
                    -{formatCurrency(totalPlatformFees)}
                  </ResultValue>
                </ResultItem>

                <ResultItem
                  style={{
                    borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                    paddingTop: '16px',
                    marginTop: '8px',
                  }}
                >
                  <ResultLabel style={{ fontWeight: 600, fontSize: '15px' }}>
                    Net Revenue
                  </ResultLabel>
                  <ResultValue
                    isPositive
                    style={{ fontSize: '16px', fontWeight: 700 }}
                  >
                    {formatCurrency(netRevenue)}
                  </ResultValue>
                </ResultItem>

                {globalEnabledScenarios.earlyBird && (
                  <SavingsCard variant="early" first>
                    <SavingsIcon variant="early">
                      <StarIcon />
                    </SavingsIcon>
                    <SavingsContent>
                      <SavingsTitle>Early Bird Savings</SavingsTitle>
                      <SavingsAmount variant="early">
                        Save {formatCurrency(earlyBirdSavings)}
                      </SavingsAmount>
                    </SavingsContent>
                    <SavingsBadge>{earlyBirdSavingsPercent}% OFF</SavingsBadge>
                  </SavingsCard>
                )}

                {globalEnabledScenarios.advance && (
                  <SavingsCard
                    variant="advance"
                    first={!globalEnabledScenarios.earlyBird}
                  >
                    <SavingsIcon variant="advance">
                      <CheckCircleIcon />
                    </SavingsIcon>
                    <SavingsContent>
                      <SavingsTitle>Standard Advance Savings</SavingsTitle>
                      <SavingsAmount variant="advance">
                        Save {formatCurrency(advanceSavings)}
                      </SavingsAmount>
                    </SavingsContent>
                    <SavingsBadge>{advanceSavingsPercent}% OFF</SavingsBadge>
                  </SavingsCard>
                )}

                <ButtonGroup>
                  <ButtonV2 onClick={handleReset}>Reset Calculator</ButtonV2>
                  <ButtonV2 primary onClick={handleExport}>
                    Export PDF Report
                  </ButtonV2>
                </ButtonGroup>
              </ResultsPanel>
            </StickyResults>
          </DashboardLayout>
        </ContentWrapper>

        <Footer>
          <Progress>Step 4 of 5</Progress>
          <FooterButtonGroup>
            <Button onClick={handleBack}>← Back</Button>
            <Button primary onClick={handleComplete}>
              Complete Guide
            </Button>
          </FooterButtonGroup>
        </Footer>

        {/* Wizard Modal */}
        <ModalV2
          isOpen={isWizardOpen && !wizardCompleted}
          onClose={handleWizardSkip}
          title="Create Your First Ticket"
          subtitle="Let's walk through setting up your first ticket type"
          maxWidth="700px"
          footer={
            <WizardFooter>
              <WizardSkipButton onClick={handleWizardSkip}>
                Skip Wizard
              </WizardSkipButton>
              <WizardButtonGroup>
                {wizardStep > 1 && (
                  <ModalButton onClick={handleWizardBack}>← Back</ModalButton>
                )}
                <ModalButton primary onClick={handleWizardNext}>
                  {wizardStep === getTotalWizardSteps() ? 'Complete' : 'Next →'}
                </ModalButton>
              </WizardButtonGroup>
            </WizardFooter>
          }
        >
          <WizardProgress>
            Step {wizardStep} of {getTotalWizardSteps()}
          </WizardProgress>
          {renderWizardStep()}
        </ModalV2>

        {/* Regular Add/Edit Ticket Modal */}
        <ModalV2
          isOpen={isModalOpen}
          onClose={handleCancelModal}
          title={editingTicketType ? 'Edit Ticket Type' : 'Add New Ticket Type'}
          subtitle="Configure pricing and quantity for this ticket type"
          maxWidth="600px"
          footer={
            <>
              <ModalButton onClick={handleCancelModal}>Cancel</ModalButton>
              <ModalButton primary onClick={handleSaveTicketType}>
                {editingTicketType ? 'Save Changes' : 'Add Ticket Type'}
              </ModalButton>
            </>
          }
        >
          <ModalFormGrid>
            <InputWrapper>
              <InputLabel>Ticket Type Name</InputLabel>
              <StyledInput
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g. General Admission, VIP, Student"
              />
            </InputWrapper>

            <ModalFormRow columns="1fr 1fr">
              <InputWrapper>
                <InputLabel>Expected Quantity</InputLabel>
                <StyledInput
                  type="number"
                  value={formData.quantity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      quantity: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </InputWrapper>

              <Select
                label="Platform Fee Type"
                value={formData.platformType}
                onChange={(e) =>
                  setFormData({ ...formData, platformType: e.target.value })
                }
                options={ticketTypeOptions}
              />
            </ModalFormRow>

            <div>
              <SectionTitle>Pricing Scenarios</SectionTitle>
              <ScenarioSelectorLabel>
                Select which pricing options to enable
              </ScenarioSelectorLabel>
              <ScenarioTagsContainer>
                {Object.entries(scenarioLabels).map(([key, label]) => (
                  <ScenarioTag
                    key={key}
                    locked={key === 'walkUp'}
                    disabled={
                      !formData.enabledScenarios[key] && key !== 'walkUp'
                    }
                  >
                    {label}
                    {key === 'walkUp' ? (
                      <LockIcon viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </LockIcon>
                    ) : formData.enabledScenarios[key] ? (
                      <RemoveScenarioButton onClick={() => toggleScenario(key)}>
                        ×
                      </RemoveScenarioButton>
                    ) : (
                      <AddScenarioButton onClick={() => toggleScenario(key)}>
                        +
                      </AddScenarioButton>
                    )}
                  </ScenarioTag>
                ))}
              </ScenarioTagsContainer>

              <ModalFormRow>
                {formData.enabledScenarios.earlyBird && (
                  <InputWrapper>
                    <InputLabel>Early Bird Price</InputLabel>
                    <StyledInput
                      type="number"
                      step="0.25"
                      value={formData.earlyBirdPrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          earlyBirdPrice: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                    <InputDescription>Discount price</InputDescription>
                  </InputWrapper>
                )}

                {formData.enabledScenarios.advance && (
                  <InputWrapper>
                    <InputLabel>Advance Price</InputLabel>
                    <StyledInput
                      type="number"
                      step="0.25"
                      value={formData.advancePrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          advancePrice: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                    <InputDescription>Regular price</InputDescription>
                  </InputWrapper>
                )}

                {formData.enabledScenarios.dayOfApp && (
                  <InputWrapper>
                    <InputLabel>Day-Of App Price</InputLabel>
                    <StyledInput
                      type="number"
                      step="0.25"
                      value={formData.dayOfAppPrice}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          dayOfAppPrice: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                    <InputDescription>Mobile purchase</InputDescription>
                  </InputWrapper>
                )}

                <InputWrapper>
                  <InputLabel>Walk-Up Price</InputLabel>
                  <StyledInput
                    type="number"
                    step="0.25"
                    value={formData.walkUpPrice}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        walkUpPrice: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                  <InputDescription>At the gate</InputDescription>
                </InputWrapper>
              </ModalFormRow>
            </div>
          </ModalFormGrid>
        </ModalV2>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default PricingCalculator;
